import {
    COLUMN_PADDING_ITEMS,
    LOOP_BLOCKS,
    WINDOW_OVERSCAN,
} from './constants';

export type ParsedTimeValue = {
    hours: number;
    minutes: number;
};

export function wrapValue(value: number, total: number): number {
    return ((value % total) + total) % total;
}

export function padTimeUnit(value: number): string {
    return value.toString().padStart(2, '0');
}

function isValidTimeParts({ hours, minutes }: ParsedTimeValue): boolean {
    return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

export function sanitizeTimeInput(value: string): string {
    const normalizedValue = value.replace(/[.,]/g, ':').replace(/[^\d:]/g, '');
    const separatorIndex = normalizedValue.indexOf(':');

    if (separatorIndex === -1) {
        return normalizedValue.replaceAll(':', '').slice(0, 4);
    }

    const hours = normalizedValue
        .slice(0, separatorIndex)
        .replaceAll(':', '')
        .slice(0, 2);
    const minutes = normalizedValue
        .slice(separatorIndex + 1)
        .replaceAll(':', '')
        .slice(0, 2);

    if (hours.length === 0) {
        return minutes;
    }

    return `${hours}:${minutes}`;
}

export function parseTimeInput(
    value: string | null | undefined
): ParsedTimeValue | null {
    if (!value) {
        return null;
    }

    const sanitizedValue = sanitizeTimeInput(value).trim();
    if (sanitizedValue.length === 0) {
        return null;
    }

    if (sanitizedValue.includes(':')) {
        const [rawHours, rawMinutes = ''] = sanitizedValue.split(':');
        if (!rawHours || rawHours.length > 2 || rawMinutes.length > 2) {
            return null;
        }

        const parts = {
            hours: parseInt(rawHours, 10),
            minutes:
                rawMinutes.length > 0
                    ? parseInt(rawMinutes.padStart(2, '0'), 10)
                    : 0,
        };

        if (Number.isNaN(parts.hours) || Number.isNaN(parts.minutes)) {
            return null;
        }

        return isValidTimeParts(parts) ? parts : null;
    }

    if (sanitizedValue.length > 4) {
        return null;
    }

    let parts: ParsedTimeValue;

    if (sanitizedValue.length <= 2) {
        parts = {
            hours: parseInt(sanitizedValue, 10),
            minutes: 0,
        };
    } else if (sanitizedValue.length === 3) {
        parts = {
            hours: parseInt(sanitizedValue.slice(0, 1), 10),
            minutes: parseInt(sanitizedValue.slice(1), 10),
        };
    } else {
        parts = {
            hours: parseInt(sanitizedValue.slice(0, 2), 10),
            minutes: parseInt(sanitizedValue.slice(2), 10),
        };
    }

    if (Number.isNaN(parts.hours) || Number.isNaN(parts.minutes)) {
        return null;
    }

    return isValidTimeParts(parts) ? parts : null;
}

export function parseTimeValue(value?: string | null): ParsedTimeValue | null {
    const parts = parseTimeInput(value);

    if (!parts) {
        return null;
    }

    return formatTimeValue(parts.hours, parts.minutes) === value ? parts : null;
}

export function formatTimeValue(hours: number, minutes: number): string {
    return `${padTimeUnit(hours)}:${padTimeUnit(minutes)}`;
}

export function getCenteredRawIndex(value: number, total: number): number {
    return Math.floor(LOOP_BLOCKS / 2) * total + value;
}

export function getCenteredWindowRange(
    currentRawIndex: number,
    maxRawIndex: number
): {
    start: number;
    end: number;
} {
    const windowRadius = COLUMN_PADDING_ITEMS + WINDOW_OVERSCAN;
    let start = Math.max(0, currentRawIndex - windowRadius);
    let end = Math.min(maxRawIndex, currentRawIndex + windowRadius);

    const missingBefore = windowRadius - (currentRawIndex - start);
    const missingAfter = windowRadius - (end - currentRawIndex);

    if (missingBefore > 0) {
        end = Math.min(maxRawIndex, end + missingBefore);
    }

    if (missingAfter > 0) {
        start = Math.max(0, start - missingAfter);
    }

    return { start, end };
}

export function easeOutCubic(progress: number): number {
    return 1 - Math.pow(1 - progress, 3);
}
